import { EditorLeftPaneContext, CodeEditorContext } from "components/utils/Contexts";
import { Console } from "console-feed";
import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useRef, useState } from "react";
import { MdRefresh } from "react-icons/md";

interface ConsolePaneType {
  consolePanelRef: React.RefObject<HTMLDivElement>;
  normalisedLeftWidth: number;
  normalisedConsoleHeight: number;
  consoleBottomRef: React.RefObject<HTMLDivElement>;
  consoleNotification: boolean;
  logs: any[];
  isConsoleOpen: boolean;
  consoleEditorSizes: number[];
  consoleLabelTransition: string;
  inputConsoleBool: boolean;
  isResizing: boolean;
  setLogs: React.Dispatch<React.SetStateAction<any[]>>;
  setInputConsoleBool: React.Dispatch<React.SetStateAction<boolean>>;
}

// Framer Motion variants for the outer transition
const containerVariants = {
  hidden: { y: 120, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

// Variants for the inner console visibility
const consoleVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.15 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

const ConsolePane: React.FC<ConsolePaneType> = ({
  normalisedLeftWidth,
  normalisedConsoleHeight,
  consoleNotification,
  consoleBottomRef,
  consolePanelRef,
  logs,
  isConsoleOpen,
  consoleEditorSizes,
  consoleLabelTransition,
  inputConsoleBool,
  isResizing,
  setLogs,
  setInputConsoleBool,
}) => {
  // States definition
  const [inputConsoleText, setInputConsoleText] = useState<string>("");
  const [inputPrompt, setInputPrompt] = useState<string>("");

  // Define a reference to the console input
  const consoleInputRef = useRef<HTMLInputElement>(null);

  // This value defines the limit of left-right paning where the opacity should be 0
  const opacityLimit = 0.3;

  // Get the codeEditorHeight
  const { codeEditorHeight } = useContext(EditorLeftPaneContext);
  const { setConsoleNotification } = useContext(CodeEditorContext);

  // Transform console editor sizes into pixels
  const consoleEditorSizesPx = consoleEditorSizes.map(
    (value: number) => (value / 100) * codeEditorHeight
  );

  /**
   * Control the behaviour of the console input when required
   */
  useEffect(() => {
    if (inputConsoleBool) {
      // If it's closed, show the notification bubble
      if (normalisedConsoleHeight === 0) {
        setConsoleNotification(true);
      }
      // Otherwise just focus the input text
      else {
        // Focus when starting
        if (consoleInputRef && consoleInputRef.current)
          consoleInputRef.current.focus();
      }
    } else {
      // Reset when finished
      setInputConsoleText("");
    }
  }, [inputConsoleBool]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "SELECT_INPUT") {
        setInputConsoleBool(true);
        setInputPrompt(event.data.prompt);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const inputPressEnter = (event: { key: string }) => {
    if (event.key === "Enter") {
      // Un-render the input text
      setInputConsoleBool(false);
      // Get the iframe and send a message
      const iframe = document.getElementById(
        "output-iframe"
      ) as HTMLIFrameElement;
      // Send the content
      if (iframe && iframe.contentWindow)
        iframe.contentWindow.postMessage(inputConsoleText, "*");
      // And send to the console
      setLogs((logs) => [
        ...logs,
        { method: "log", data: [`${inputPrompt}${inputConsoleText}`] },
      ]);
    }
  };

  /**
   * Function executed when clicking the clear console button
   */
  const handleClearConsole = () => {
    // Clear the logs
    setLogs([]);

    // And force to close the input boolean (if there's any opened)
    setInputConsoleBool(false);
  };

  return (
    <div
      className={`flex flex-row w-full h-full rounded-xl ${
        isResizing ? "pointer-events-none" : ""
      }`}
      ref={consolePanelRef}
    >
      <div className="flex-none w-12 h-full mr-auto z-20">
        <div className="w-fit h-fit translate-x-[-2.4rem] text-neutral-700 select-none">
          <motion.div
            className="whitespace-nowrap inline-flex"
            style={{
              originX: 1,
              originY: 0,
              opacity: !isConsoleOpen
                ? 1
                : -0.5 *
                    Math.min(normalisedLeftWidth, normalisedConsoleHeight) +
                  1,
              rotate: !isConsoleOpen
                ? -90 * (1 - normalisedLeftWidth)
                : Math.round(
                    // The rotation effect of paning down, compensated
                    // with the left width pane movement. i.e. if the normalissedLeftWidth
                    // is 0 (or closed), we don't want to see the effect of the top-down
                    // movement)
                    -90 * normalisedConsoleHeight * normalisedLeftWidth +
                      // The rotation effect of shrinking the pane when moving left
                      -90 * (1 - normalisedLeftWidth)
                  ),
              translateX: !isConsoleOpen
                ? 100 * normalisedLeftWidth + (1 - normalisedLeftWidth) * 31
                : Math.round(
                    // The effect of shrinking the pane when moving left
                    (1 - normalisedLeftWidth) * 31 +
                      // The corrected (because is faded by the left-rigth effect)
                      // effect of shrinking the pane when moving down
                      (1 - normalisedConsoleHeight) * 100 * normalisedLeftWidth
                  ),
              translateY: !isConsoleOpen
                ? ((consoleEditorSizesPx[1] - 27) / 2) * normalisedLeftWidth +
                  (1 - normalisedLeftWidth) * 16
                : Math.round(
                    // The effect of shrinking the pane when moving left
                    (1 - normalisedLeftWidth) * 12 +
                      // The corrected (because is faded by the left-rigth effect)
                      // effect of shrinking the pane when moving down
                      (1 - normalisedConsoleHeight) * 9 * normalisedLeftWidth +
                      // A correcting value just to align the text better
                      3
                  ),
              transition: consoleLabelTransition,
            }}
          >
            Console
            <AnimatePresence>
              {consoleNotification && normalisedLeftWidth === 1 && (
                <motion.div
                  key="notification"
                  initial={{ opacity: 0 }}
                  className="w-4 h-4 px-2 bg-teal-500 rounded-full"
                  animate={{ opacity: 1, scale: [1, 1.5, 1, 1.5, 1] }}
                  transition={{
                    opacity: { duration: 0.15 },
                    scale: {
                      duration: 1,
                      ease: "easeInOut",
                      times: [0, 0.2, 0.4, 0.6, 1],
                    },
                  }}
                  exit={{ opacity: 0 }}
                />
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
      <div className="flex-1 flex flex-col h-full pr-4 py-4 bg-Canvas rounded-xl overflow-hidden relative z-10">
        <AnimatePresence>
          {logs.length > 0 && (
            <motion.div
              key="clear-log-container"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, scale: [1, 1.2, 1, 1.2, 1] }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 0.3 },
                scale: {
                  duration: 1,
                  ease: "easeInOut",
                  times: [0, 0.2, 0.4, 0.6, 1],
                },
              }}
              className="absolute flex justify-end w-fit right-0 px-4 z-20 overflow-visible"
            >
              <AnimatePresence>
                {isConsoleOpen && (
                  <motion.div
                    initial={{ y: -120, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{
                      y: { duration: 0.5 },
                      opacity: { duration: 0.3 },
                    }}
                    exit={{ opacity: 0 }}
                    className="px-2 overflow-hidden"
                  >
                    <button
                      key="clear-console"
                      className="flex justify-center items-center w-11 h-11 bg-neutral-450 hover:bg-neutral-600 ease-in-out duration-150 rounded-full cursor-pointer"
                      title="Clear console"
                      onClick={handleClearConsole}
                      style={{
                        opacity: Math.max(
                          Math.min(
                            normalisedConsoleHeight,
                            1 +
                              (1 / (1 - opacityLimit)) *
                                (normalisedLeftWidth - 1)
                          ),
                          0
                        ),
                      }}
                    >
                      <MdRefresh className="w-6 h-6 fill-neutral-50" />
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {isConsoleOpen && normalisedLeftWidth > 0 && (
            <motion.div
              key="console-container"
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={containerVariants}
              className="w-full h-full custom-scroll-Canvas pr-9"
              style={{ overflow: normalisedLeftWidth < 1 ? "hidden" : "" }}
            >
              <motion.div
                style={{
                  opacity: Math.max(
                    Math.min(
                      normalisedConsoleHeight,
                      1 + (1 / (1 - opacityLimit)) * (normalisedLeftWidth - 1)
                    ),
                    0
                  ),
                }}
              >
                <AnimatePresence>
                  {logs && logs.length > 0 && (
                    <motion.div
                      key="console"
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      variants={consoleVariants}
                      // style={{ overflow: "hidden" }}
                      className="text-wrap whitespace-pre-wrap"
                    >
                      <Console
                        logs={logs}
                        logGrouping={false}
                        styles={{
                          BASE_FONT_FAMILY:
                            "Source Code Pro, Consolas, Lucida Console, Courier New, monospace",
                          BASE_LINE_HEIGHT: 16,
                          BASE_FONT_SIZE: 16,
                          TABLE_BORDER_COLOR: "rgba(0, 0, 0, 0)",
                        }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                <div id="consoleBottom" ref={consoleBottomRef}>
                  {inputConsoleBool && (
                    <div className="w-full h-6 px-8 py-0.5 flex">
                      <div className="w-fit flex items-center font-mono break-keep whitespace-nowrap">
                        {inputPrompt.replace(" ", "\u00A0")}
                      </div>
                      <input
                        ref={consoleInputRef}
                        type="text"
                        id="console-input-text"
                        className="w-full px-1 focus:outline-none font-mono bg-transparent text-PureBlue caret-PureBlue"
                        onChange={(event) =>
                          setInputConsoleText(event.target.value)
                        }
                        onKeyDown={inputPressEnter}
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ConsolePane;
